import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Sparkles,
  Package,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { AnswerRenderer } from '@/components/AnswerRenderer';

interface GeneratedAnswer {
  id: string;
  question: string;
  answer: string;
  generation_type: string;
  context_used: any[];
  model: string;
  confidence_score: number | null;
  approval_status: string;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

interface Job {
  id: string;
  generation_type: string;
  status: string;
  total_questions: number;
  processed_questions: number;
  approved_answers: number;
  rejected_answers: number;
}

export default function AnswerApproval() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const [job, setJob] = useState<Job | null>(null);
  const [answers, setAnswers] = useState<GeneratedAnswer[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<Set<string>>(new Set());
  const [expandedAnswers, setExpandedAnswers] = useState<Set<string>>(new Set());
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hideNoInfoAnswers, setHideNoInfoAnswers] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
      fetchAnswers();
    }
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await authFetch(`/api/answer-generation/jobs/${jobId}`);
      if (!response.ok) throw new Error('Failed to fetch job details');

      const data = await response.json();
      setJob(data.job);
    } catch (err) {
      console.error('Error fetching job:', err);
      setError(err instanceof Error ? err.message : 'Failed to load job');
    }
  };

  const fetchAnswers = async () => {
    try {
      setLoading(true);
      const response = await authFetch(`/api/answer-generation/jobs/${jobId}/answers`);
      if (!response.ok) throw new Error('Failed to fetch answers');

      const data = await response.json();
      setAnswers(data.answers || []);
    } catch (err) {
      console.error('Error fetching answers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load answers');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (answerId: string) => {
    setProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch(`/api/answer-generation/answers/${answerId}/approve`, {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve answer');
      }

      setSuccess('Answer approved and added to knowledge base!');
      fetchAnswers();
      fetchJobDetails();
    } catch (err) {
      console.error('Error approving answer:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve answer');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (answerId: string) => {
    const reason = rejectReason[answerId];
    if (!reason || reason.trim() === '') {
      setError('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch(`/api/answer-generation/answers/${answerId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reject answer');
      }

      setSuccess('Answer rejected');
      fetchAnswers();
      fetchJobDetails();
      setRejectReason({ ...rejectReason, [answerId]: '' });
    } catch (err) {
      console.error('Error rejecting answer:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject answer');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedAnswers.size === 0) {
      setError('Please select answers to approve');
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authFetch('/api/answer-generation/answers/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answerIds: Array.from(selectedAnswers) })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to bulk approve');
      }

      const data = await response.json();
      setSuccess(`Successfully approved ${data.approved} answers!`);
      setSelectedAnswers(new Set());
      fetchAnswers();
      fetchJobDetails();
    } catch (err) {
      console.error('Error bulk approving:', err);
      setError(err instanceof Error ? err.message : 'Failed to bulk approve');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelection = (answerId: string) => {
    const newSelected = new Set(selectedAnswers);
    if (newSelected.has(answerId)) {
      newSelected.delete(answerId);
    } else {
      newSelected.add(answerId);
    }
    setSelectedAnswers(newSelected);
  };

  const toggleExpanded = (answerId: string) => {
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(answerId)) {
      newExpanded.delete(answerId);
    } else {
      newExpanded.add(answerId);
    }
    setExpandedAnswers(newExpanded);
  };

  const selectAll = () => {
    const pendingAnswers = answers.filter(a => a.approval_status === 'pending').map(a => a.id);
    setSelectedAnswers(new Set(pendingAnswers));
  };

  // Helper function to detect "no info found" answers
  const isNoInfoFoundAnswer = (answer: string): boolean => {
    const lowerAnswer = answer.toLowerCase();
    const noInfoPatterns = [
      'no encuentro información',
      'no tengo información',
      'no dispongo de información',
      'no cuento con información',
      'no hay información disponible',
      'no dispongo de datos',
      'i don\'t have information',
      'i cannot find information',
      'no information available',
      'información no disponible',
      'no se encuentra información',
      'en nuestro catálogo actual'
    ];

    return noInfoPatterns.some(pattern => lowerAnswer.includes(pattern));
  };

  // Filter answers based on approval status and "no info" filter
  const pendingAnswers = answers
    .filter(a => a.approval_status === 'pending')
    .filter(a => !hideNoInfoAnswers || !isNoInfoFoundAnswer(a.answer));
  const approvedAnswers = answers.filter(a => a.approval_status === 'approved');
  const rejectedAnswers = answers.filter(a => a.approval_status === 'rejected');

  // Count how many "no info" answers are hidden
  const allPending = answers.filter(a => a.approval_status === 'pending');
  const noInfoCount = allPending.filter(a => isNoInfoFoundAnswer(a.answer)).length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/answer-generation')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Review Generated Answers</h1>
          {job && (
            <p className="text-muted-foreground">
              {job.generation_type === 'generic_ai' ? 'Generic AI' : 'Product Facts'} • {job.processed_questions} questions processed
            </p>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Summary Stats */}
      {job && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{answers.length}</div>
              <div className="text-sm text-muted-foreground">Total Answers</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{pendingAnswers.length}</div>
              <div className="text-sm text-muted-foreground">Pending Review</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{approvedAnswers.length}</div>
              <div className="text-sm text-muted-foreground">Approved</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-red-600">{rejectedAnswers.length}</div>
              <div className="text-sm text-muted-foreground">Rejected</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Actions and Filters */}
      {allPending.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Filter Controls */}
              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hideNoInfo"
                    checked={hideNoInfoAnswers}
                    onCheckedChange={(checked) => setHideNoInfoAnswers(checked === true)}
                  />
                  <label
                    htmlFor="hideNoInfo"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Hide "No info found" answers
                    {noInfoCount > 0 && (
                      <span className="ml-2 text-muted-foreground">
                        ({noInfoCount} questions)
                      </span>
                    )}
                  </label>
                </div>
              </div>

              {/* Bulk Actions */}
              {pendingAnswers.length > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={selectAll}>
                      Select All Pending
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {selectedAnswers.size} selected
                    </span>
                  </div>
                  <Button
                    onClick={handleBulkApprove}
                    disabled={selectedAnswers.size === 0 || processing}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Approve Selected ({selectedAnswers.size})
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Answers List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading answers...</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Pending Answers */}
          {pendingAnswers.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Pending Review ({pendingAnswers.length})</h2>
              {pendingAnswers.map((answer) => (
                <Card key={answer.id} className="border-yellow-200">
                  <CardContent className="pt-6 space-y-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedAnswers.has(answer.id)}
                        onCheckedChange={() => toggleSelection(answer.id)}
                      />
                      <div className="flex-1 space-y-3">
                        {/* Question */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">Question</Badge>
                            {answer.generation_type === 'product_facts' && (
                              <Package className="w-4 h-4 text-primary" />
                            )}
                          </div>
                          <p className="font-medium">{answer.question}</p>
                        </div>

                        {/* Answer */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">Answer</Badge>
                            <span className="text-xs text-muted-foreground">({answer.model})</span>
                          </div>
                          <div className={expandedAnswers.has(answer.id) ? '' : 'max-h-48 overflow-hidden relative'}>
                            <AnswerRenderer answer={answer.answer} />
                            {!expandedAnswers.has(answer.id) && answer.answer.length > 200 && (
                              <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
                            )}
                          </div>
                          {answer.answer.length > 200 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpanded(answer.id)}
                              className="mt-2"
                            >
                              {expandedAnswers.has(answer.id) ? (
                                <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
                              ) : (
                                <>Show More <ChevronDown className="w-4 h-4 ml-1" /></>
                              )}
                            </Button>
                          )}
                        </div>

                        {/* Context Used */}
                        {answer.context_used && answer.context_used.length > 0 && (
                          <div>
                            <Badge variant="secondary" className="mb-2">
                              Context: {answer.context_used.length} source(s)
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              {answer.context_used.map((ctx: any, i: number) => (
                                <div key={i}>• {ctx.title || ctx.type}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(answer.id)}
                            disabled={processing}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <div className="flex-1 flex items-center gap-2">
                            <Textarea
                              placeholder="Rejection reason..."
                              value={rejectReason[answer.id] || ''}
                              onChange={(e) => setRejectReason({ ...rejectReason, [answer.id]: e.target.value })}
                              className="h-9 text-sm"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(answer.id)}
                              disabled={processing}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Approved Answers */}
          {approvedAnswers.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-green-600">Approved ({approvedAnswers.length})</h2>
              {approvedAnswers.map((answer) => (
                <Card key={answer.id} className="border-green-200 bg-green-50/50">
                  <CardContent className="pt-6">
                    <div className="space-y-2">
                      <p className="font-medium">{answer.question}</p>
                      <div className="max-h-32 overflow-hidden relative">
                        <AnswerRenderer answer={answer.answer} />
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-green-50 to-transparent" />
                      </div>
                      <Badge className="bg-green-600">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        In Knowledge Base
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Rejected Answers */}
          {rejectedAnswers.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-red-600">Rejected ({rejectedAnswers.length})</h2>
              {rejectedAnswers.map((answer) => (
                <Card key={answer.id} className="border-red-200 bg-red-50/50">
                  <CardContent className="pt-6">
                    <div className="space-y-2">
                      <p className="font-medium">{answer.question}</p>
                      {answer.review_notes && (
                        <p className="text-sm text-red-700">Reason: {answer.review_notes}</p>
                      )}
                      <Badge variant="destructive">
                        <XCircle className="w-3 h-3 mr-1" />
                        Rejected
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {answers.length === 0 && !loading && (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No answers generated yet</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
